import { SHARE_LINK_DEFAULT_DAYS, formatDate, formatDateTime } from '@erp/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Copy, Link2, MessageCircle } from 'lucide-react';
import { useState } from 'react';

import { api, errorMessage } from '../../lib/api.ts';
import {
  propertyKeys,
  useShareLinks,
  type PropertyDetail,
  type ShareLink,
} from '../../lib/properties-api.ts';
import { useSession } from '../../lib/session.ts';
import { useToast } from '../../lib/toast.tsx';
import { Badge } from '../ui/badge.tsx';
import { Button, buttonVariants } from '../ui/button.tsx';
import { Card } from '../ui/card.tsx';
import { Field, Input, Select } from '../ui/field.tsx';

const DAY_OPTIONS = [7, 30, 90, 180] as const;

/**
 * HU-INV-10: an opaque link to share the listing with a client (no exact address,
 * no private data). The full link is shown once, when created; after that only its
 * visits and expiry are listed (the token is not stored).
 */
export function ShareLinksCard({ property }: { property: PropertyDetail }) {
  const { data: session } = useSession();
  const tz = session?.tenant.timezone;
  const client = useQueryClient();
  const notify = useToast();
  const links = useShareLinks(property.id);
  const [days, setDays] = useState<number>(SHARE_LINK_DEFAULT_DAYS);
  const [created, setCreated] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = (list: ShareLink[]) => {
    client.setQueryData(propertyKeys.shareLinks(property.id), list);
  };
  const create = async () => {
    setBusy(true);
    try {
      const result = await api<{ url: string; link: ShareLink }>(
        `/properties/${property.id}/share-links`,
        { method: 'POST', body: { days } },
      );
      setCreated(result.url);
      await client.invalidateQueries({ queryKey: propertyKeys.shareLinks(property.id) });
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setBusy(false);
    }
  };
  const act = async (link: ShareLink, action: 'extend' | 'revoke') => {
    try {
      refresh(
        await api<ShareLink[]>(`/properties/${property.id}/share-links/${link.id}/${action}`, {
          method: 'POST',
          body: action === 'extend' ? { days: SHARE_LINK_DEFAULT_DAYS } : {},
        }),
      );
      notify(
        action === 'extend'
          ? `Enlace extendido ${String(SHARE_LINK_DEFAULT_DAYS)} días desde hoy.`
          : 'Enlace revocado: ya no abre.',
        'success',
      );
    } catch (error) {
      notify(errorMessage(error), 'error');
    }
  };
  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      notify('Enlace copiado.', 'success');
    } catch {
      notify('No se pudo copiar: selecciona el enlace y cópialo a mano.', 'error');
    }
  };
  const message = (url: string) =>
    `Hola, te comparto esta propiedad: ${property.title} (${property.code}). ${url}`;

  return (
    <Card className="space-y-4">
      <div>
        <h2 className="font-semibold">Compartir con un cliente</h2>
        <p className="text-sm text-muted-foreground">
          Un enlace sin la dirección exacta ni datos privados, con tus datos de contacto. Crea uno
          por cliente para saber cuántas veces lo abrió.
        </p>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <Field label="Vigencia">
          {(a11y) => (
            <Select
              {...a11y}
              className="sm:w-40"
              value={days}
              onChange={(e) => {
                setDays(Number(e.target.value));
              }}
            >
              {DAY_OPTIONS.map((d) => (
                <option key={d} value={d}>
                  {d} días
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Button variant="secondary" loading={busy} onClick={() => void create()}>
          <Link2 /> Crear enlace
        </Button>
      </div>
      {created && (
        <div className="space-y-2 rounded-lg border border-border bg-muted p-3" role="status">
          <p className="text-sm font-medium">
            Enlace listo: cópialo ahora (no se vuelve a mostrar).
          </p>
          <Input
            readOnly
            aria-label="Enlace para compartir"
            value={created}
            onFocus={(e) => {
              e.target.select();
            }}
          />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="brand" onClick={() => void copy(created)}>
              <Copy /> Copiar
            </Button>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(message(created))}`}
              target="_blank"
              rel="noopener noreferrer"
              className={buttonVariants({ size: 'sm', variant: 'secondary' })}
            >
              <MessageCircle /> Enviar por WhatsApp
            </a>
          </div>
        </div>
      )}
      {(links.data?.length ?? 0) > 0 && (
        <ul className="divide-y divide-border text-sm">
          {links.data?.map((link) => (
            <li key={link.id} className="flex flex-col gap-2 py-2 sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1 space-y-0.5">
                <p className="flex flex-wrap items-center gap-1.5">
                  {link.active ? (
                    <Badge tone="success">Activo</Badge>
                  ) : (
                    <Badge>{link.revokedAt ? 'Revocado' : 'Vencido'}</Badge>
                  )}
                  <span>
                    {link.viewCount === 1 ? '1 visita' : `${String(link.viewCount)} visitas`}
                    {link.lastViewedAt ? ` · última ${formatDateTime(link.lastViewedAt, tz)}` : ''}
                  </span>
                </p>
                <p className="text-xs text-muted-foreground">
                  Creado el {formatDate(link.createdAt, tz)} por {link.createdBy} ·{' '}
                  {link.revokedAt
                    ? `revocado el ${formatDate(link.revokedAt, tz)}`
                    : `vence el ${formatDate(link.expiresAt, tz)}`}
                </p>
              </div>
              {!link.revokedAt && (
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => void act(link, 'extend')}>
                    Extender
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => void act(link, 'revoke')}>
                    Revocar
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
